/**
 * Das Urteil EINER Zeile der Dokumente-Checkliste im Drawer: Zustand, Pflicht und Sichtbarkeit.
 *
 * Neucode ohne Vorbild in `index.html` (Spec 2026-11-03-drawer-checkliste-gleichlauf): Gegenstueck zu `bewerteCheckliste`
 * (`backend/src/domaene/checkliste.ts`), das Board, Banner und Archivierung mit demselben Ergebnis zaehlen. Dieses Modul fuehrt
 * KEINE eigene Pflichtregel: Pflicht, Sichtbarkeit und Erfuellung (auch ein Beleg an der Ist-Zahlung, auch eine geerbte
 * Datei) kommen aus dem geladenen Eintrag des Servers (`GET /api/orders/:id/document-checklist`).
 *
 * Zwei Faelle:
 *
 * - **Serverfall** (Checkliste geladen, Eintrag zum Typ da, Zustand nicht unbekannt): der Eintrag entscheidet. Nur bei der
 *   Sichtbarkeit blendet der Drawer nie mehr aus als vorher: eine Zeile, die er bisher zeigte (`visibleDocTypes`), eine mit
 *   Datei und eine mit Haken verschwindet nicht, bloss weil der Server sie `ausgeblendet` nennt.
 * - **Rueckfall** (Checkliste laedt noch, Laden gescheitert, `jtl-<n>` ohne Hub-Zeile, Datei-/Checklistenliste unbekannt): das
 *   Bild von vorher - Datei heisst ok, sonst offen, Pflicht nach der Konfiguration des Typs (`optional`), Sichtbarkeit nach
 *   `visibleDocTypes`. Eine unbekannte Lage (`zustandUnbekannt`) zeigt keinen Zustand.
 *
 * Reine Funktion; sie importiert nur Typen (kein `state/fehlerStapel.ts`: die Checkliste liegt in einem Chunk des Buendels,
 * siehe `AGENTS.md`, Fallen, Rolldown).
 */
import type { DocType } from "../../../config/documents.ts";
import type { ChecklistenEintrag } from "../../../lib/types.ts";

export type ZeilenZustand = "ok" | "na" | "offen" | "unbekannt";

export interface ZeilenUrteil {
  /** Ob die Zeile gezeichnet wird. */
  sichtbar: boolean;
  /** Erfuellt (`ok`), als nicht notwendig abgehakt (`na`), offen oder unbekannt (kein Zustand zeigen). */
  zustand: ZeilenZustand;
  /** Pflichtzeile (ohne "optional"-Marke, Knopf "manuell abhaken" statt "Nicht notwendig"). */
  pflicht: boolean;
}

export function zeilenUrteil(e: {
  typ: DocType;
  /** Der Eintrag des Servers zu diesem Typ; `undefined`, solange die Checkliste fehlt oder keine Zeile fuer den Typ hat. */
  eintrag: ChecklistenEintrag | undefined;
  /** Prop `checkliste !== null` der Checkliste: der Server hat geantwortet. */
  checklisteGeladen: boolean;
  /** Wie bisher: Dokumentliste oder Checkliste konnten nicht geladen werden. */
  zustandUnbekannt: boolean;
  /** Dateien dieses Typs aus der Dokumentliste. */
  dateien: number;
  /** `visibleDocTypes(o)` enthaelt den Typ. */
  rueckfallSichtbar: boolean;
}): ZeilenUrteil {
  const { eintrag } = e;
  if (e.checklisteGeladen && eintrag && !e.zustandUnbekannt) {
    return {
      sichtbar:
        e.rueckfallSichtbar || eintrag.sichtbarkeit !== "ausgeblendet" || e.dateien > 0 || eintrag.status !== "offen",
      zustand:
        eintrag.status === "vorhanden"
          ? "ok"
          : eintrag.status === "entfaellt"
            ? e.dateien > 0 ? "ok" : "na"
            : "offen",
      pflicht: eintrag.sichtbarkeit === "pflicht",
    };
  }
  return {
    sichtbar: e.rueckfallSichtbar,
    zustand: e.zustandUnbekannt
      ? "unbekannt"
      : e.dateien > 0
        ? "ok"
        : eintrag?.status === "entfaellt"
          ? "na"
          : "offen",
    pflicht: !e.typ.optional,
  };
}
